import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { authClient } from "@/lib/auth-client";

// Maximum number of managers
const MAX_MANAGERS = 5;

// Validation schema - only email is required now
// Note: firstName, lastName, phone are hidden for now pending product decision
const addManagerSchema = z.object({
  email: z.string().email("Format d'email invalide"),
});

type AddManagerFormValues = z.infer<typeof addManagerSchema>;

interface AddManagerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  existingEmails: string[];
  managersCount: number;
}

export function AddManagerSheet({
  open,
  onOpenChange,
  onSuccess,
  existingEmails,
  managersCount,
}: AddManagerSheetProps) {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<AddManagerFormValues>({
    resolver: zodResolver(addManagerSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: AddManagerFormValues) => {
    // Check max managers limit
    if (managersCount >= MAX_MANAGERS) {
      toast.error("Limite atteinte", {
        description: `Vous ne pouvez pas ajouter plus de ${MAX_MANAGERS} gérants.`,
      });
      return;
    }

    // Check for duplicate email
    if (existingEmails.includes(data.email.toLowerCase())) {
      form.setError("email", {
        type: "manual",
        message: "Cet email est déjà utilisé par un autre gérant",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Use better-auth to invite the member as admin
      const result = await authClient.organization.inviteMember({
        email: data.email,
        role: "admin",
      });

      if (result.error) {
        // Handle specific error cases
        if (result.error.message?.includes("already a member")) {
          form.setError("email", {
            type: "manual",
            message: "Cette personne est déjà membre de l'organisation",
          });
        } else if (result.error.message?.includes("already invited")) {
          form.setError("email", {
            type: "manual",
            message: "Une invitation a déjà été envoyée à cette adresse",
          });
        } else {
          toast.error("Erreur lors de l'envoi de l'invitation", {
            description: result.error.message,
          });
        }
        return;
      }

      form.reset();
      onOpenChange(false);
      onSuccess();
      toast.success("Invitation envoyée", {
        description: `Un email d'invitation a été envoyé à ${data.email}`,
      });
    } catch {
      toast.error("Erreur lors de l'envoi de l'invitation");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="h-screen max-w-md! overflow-y-auto [&>button]:hidden">
        <SheetHeader className="sticky top-0 z-2 border-b bg-white shadow-xs">
          <SheetTitle>Inviter un gérant</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4 px-8">
            <p className="text-sm text-muted-foreground">
              Entrez l'adresse email de la personne que vous souhaitez inviter comme gérant. Elle recevra un email
              d'invitation pour rejoindre votre club.
            </p>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="email@exemple.com" autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter className="mt-8 px-8 gap-2">
              <SheetClose asChild>
                <Button type="button" variant="outline">
                  Annuler
                </Button>
              </SheetClose>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  "Envoyer l'invitation"
                )}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
